/**
 * configUtils.js
 * 
 * Utilities for application configuration management.
 * Handles storing and retrieving configuration from localStorage.
 */

const CONFIG_KEY = 'ai_code_implementer_config';

/**
 * Default application configuration
 */
const DEFAULT_CONFIG = {
  editor: {
    theme: 'vs-dark',
    fontSize: 14,
    tabSize: 2,
    wordWrap: true,
    minimap: true,
    lineNumbers: true,
    formatOnSave: true
  },
  ui: {
    sidebarWidth: 250,
    editorWidth: 600,
    showPanels: true,
    colorTheme: 'dark'
  },
  ai: {
    maxResponseTokens: 4000,
    temperature: 0.7,
    modelName: 'gpt-4',
    promptStrategy: 'comprehensive'
  },
  storage: {
    backupInterval: 300000 // 5 minutes
  },
  projects: {
    recentProjects: [],
    maxRecentProjects: 10
  }
};

/**
 * Load configuration from localStorage
 * @returns {Object} Application configuration
 */
export function loadConfig() {
  try {
    const storedConfig = localStorage.getItem(CONFIG_KEY);
    if (!storedConfig) {
      return DEFAULT_CONFIG;
    }
    
    // Parse stored configuration
    const parsedConfig = JSON.parse(storedConfig);
    
    // Merge with default to ensure all properties exist
    return {
      editor: { ...DEFAULT_CONFIG.editor, ...parsedConfig.editor },
      ui: { ...DEFAULT_CONFIG.ui, ...parsedConfig.ui },
      ai: { ...DEFAULT_CONFIG.ai, ...parsedConfig.ai },
      storage: { ...DEFAULT_CONFIG.storage, ...parsedConfig.storage },
      projects: { ...DEFAULT_CONFIG.projects, ...parsedConfig.projects }
    };
  } catch (error) {
    console.error('Error loading configuration:', error);
    return DEFAULT_CONFIG;
  }
}

/**
 * Save configuration to localStorage
 * @param {Object} config - Updated configuration
 * @returns {boolean} Success status
 */
export function saveConfig(config) {
  try {
    const mergedConfig = {
      ...loadConfig(),
      ...config
    };
    
    localStorage.setItem(CONFIG_KEY, JSON.stringify(mergedConfig));
    return true;
  } catch (error) {
    console.error('Error saving configuration:', error);
    return false;
  }
}

/**
 * Update a specific section of the configuration
 * @param {string} section - Section name (editor, ui, ai, storage, projects)
 * @param {Object} values - Updated values
 * @returns {boolean} Success status
 */
export function updateConfigSection(section, values) {
  try {
    const currentConfig = loadConfig();
    
    if (!currentConfig[section]) {
      throw new Error(`Invalid configuration section: ${section}`);
    }
    
    const updatedConfig = {
      ...currentConfig,
      [section]: {
        ...currentConfig[section],
        ...values
      }
    };
    
    return saveConfig(updatedConfig);
  } catch (error) {
    console.error('Error updating configuration section:', error);
    return false;
  }
}

/**
 * Reset configuration to defaults
 * @param {string|null} section - Section to reset, or null for all
 * @returns {boolean} Success status
 */
export function resetConfig(section = null) {
  try {
    if (section) {
      // Reset only specified section
      if (!DEFAULT_CONFIG[section]) {
        throw new Error(`Invalid configuration section: ${section}`);
      }
      
      return updateConfigSection(section, DEFAULT_CONFIG[section]);
    } else {
      // Reset entire configuration
      localStorage.setItem(CONFIG_KEY, JSON.stringify(DEFAULT_CONFIG));
      return true;
    }
  } catch (error) {
    console.error('Error resetting configuration:', error);
    return false;
  }
}

/**
 * Add a project to recent projects list
 * @param {Object} project - Project to add
 * @returns {boolean} Success status
 */
export function addRecentProject(project) {
  try {
    const { id, name, updated } = project;
    const config = loadConfig();
    
    // Filter out this project if it already exists
    let recentProjects = config.projects.recentProjects.filter(
      p => p.id !== id
    );
    
    // Add project to the beginning of the array
    recentProjects.unshift({ id, name, updated });
    
    // Limit to max number of recent projects
    recentProjects = recentProjects.slice(0, config.projects.maxRecentProjects);
    
    // Update configuration
    return updateConfigSection('projects', { recentProjects });
  } catch (error) {
    console.error('Error adding recent project:', error);
    return false;
  }
}

/**
 * Remove a project from recent projects list
 * @param {string} projectId - Project ID to remove
 * @returns {boolean} Success status
 */
export function removeRecentProject(projectId) {
  try {
    const config = loadConfig();
    
    // Filter out the project
    const recentProjects = config.projects.recentProjects.filter(
      p => p.id !== projectId
    );
    
    // Update configuration
    return updateConfigSection('projects', { recentProjects });
  } catch (error) {
    console.error('Error removing recent project:', error);
    return false;
  }
}

/**
 * Get recent projects list
 * @returns {Array} Recent projects
 */
export function getRecentProjects() {
  const config = loadConfig();
  return config.projects.recentProjects || [];
}

/**
 * Check if local storage is available
 * @returns {boolean} Storage availability
 */
export function isLocalStorageAvailable() {
  try {
    const testKey = 'test_storage';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Check if IndexedDB is available
 * @returns {Promise<boolean>} Storage availability
 */
export async function isIndexedDBAvailable() {
  if (!window.indexedDB) {
    return false;
  }
  
  try {
    const testDb = indexedDB.open('test_db', 1);
    
    return new Promise((resolve) => {
      testDb.onerror = () => resolve(false);
      testDb.onsuccess = () => {
        testDb.result.close();
        indexedDB.deleteDatabase('test_db');
        resolve(true);
      };
    });
  } catch (e) {
    return false;
  }
}

/**
 * Estimate available storage space
 * @returns {Promise<number>} Estimated space in MB
 */
export async function estimateAvailableStorage() {
  if (navigator.storage && navigator.storage.estimate) {
    try {
      const { quota, usage } = await navigator.storage.estimate();
      const availableMB = Math.round((quota - usage) / (1024 * 1024));
      return availableMB;
    } catch (e) {
      console.error('Error estimating storage:', e);
      return null;
    }
  }
  return null;
}
